import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Table, TableHead,
  TableRow, TableCell, TableBody, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, CircularProgress, Alert, IconButton,
  InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Tooltip, TableContainer, TablePagination,
} from '@mui/material';
import {
  Inventory as InventoryIcon, Warning, AddCircle, Remove, History,
  Search, Refresh,
} from '@mui/icons-material';
import {
  fetchInventory, fetchCategories, restockProduct, adjustStock,
  fetchInventoryHistory, fetchLowStock,
} from '../../api/api';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;

const STATUS = (stock) => {
  if (stock === 0) return { label: 'Out of Stock', color: 'error' };
  if (stock <= 5) return { label: 'Low Stock', color: 'warning' };
  return { label: 'OK', color: 'success' };
};

const LIMIT = 50;

export default function InventoryManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed

  /* filters */
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const searchRef = useRef('');
  const debounceTimer = useRef(null);

  /* ── summary stats derived from current page ── */
  const outOfStock = products.filter((p) => (p.stock ?? 0) === 0).length;
  const totalValue = products.reduce((s, p) => s + (p.stock ?? 0) * (p.costPrice ?? p.price ?? 0), 0);

  /* ── load paginated inventory ── */
  const load = useCallback(async ({ pg = 0, srch = search, cat = catFilter, status = statusFilter } = {}) => {
    try {
      setLoading(true);
      setError('');
      const params = { page: pg + 1, limit: LIMIT }; // backend is 1-indexed
      if (srch.trim()) params.search = srch.trim();
      if (cat) params.category = cat;
      if (status === 'low' || status === 'out') params.lowStock = 'true';
      const res = await fetchInventory(params);
      const raw = res.data;
      const prods = Array.isArray(raw?.products) ? raw.products : [];
      setProducts(prods.map((p) => ({ ...p, name: p.name || p.title || '' })));
      setTotal(raw?.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inventory.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── one-time: categories + low-stock count ── */
  useEffect(() => {
    fetchCategories()
      .then((res) => {
        const raw = res.data;
        setCategories(Array.isArray(raw) ? raw : (raw?.categories ?? raw?.data ?? []));
      })
      .catch(() => {});
    fetchLowStock()
      .then((res) => {
        const raw = res.data;
        const arr = Array.isArray(raw?.products) ? raw.products : (Array.isArray(raw) ? raw : []);
        setLowStockCount(arr.length);
      })
      .catch(() => {});
  }, []);

  /* ── initial load + page change ── */
  useEffect(() => {
    load({ pg: page });
  }, [page, load]);

  /* ── filter handlers — reset to page 0 and reload ── */
  const handleCat = (val) => {
    setCatFilter(val);
    setPage(0);
    load({ pg: 0, cat: val });
  };
  const handleStatus = (val) => {
    setStatusFilter(val);
    setPage(0);
    load({ pg: 0, status: val });
  };
  const handleSearch = (val) => {
    setSearch(val);
    searchRef.current = val;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(0);
      load({ pg: 0, srch: searchRef.current });
    }, 400);
  };

  /* ── restock dialog ── */
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockTarget, setRestockTarget] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [restockCost, setRestockCost] = useState('');
  const [restockNote, setRestockNote] = useState('');
  const [restockLoading, setRestockLoading] = useState(false);
  const [restockError, setRestockError] = useState('');

  const openRestock = (product) => {
    setRestockTarget(product);
    setRestockQty('');
    setRestockCost('');
    setRestockNote('');
    setRestockError('');
    setRestockOpen(true);
  };

  const handleRestock = async () => {
    if (!restockQty || Number(restockQty) <= 0) { setRestockError('Enter a valid quantity.'); return; }
    try {
      setRestockLoading(true);
      setRestockError('');
      await restockProduct(restockTarget._id, {
        quantity: Number(restockQty),
        unitCost: restockCost ? Number(restockCost) : undefined,
        note: restockNote,
      });
      setRestockOpen(false);
      load({ pg: page });
    } catch (err) {
      setRestockError(err.response?.data?.message || 'Failed to restock.');
    } finally {
      setRestockLoading(false);
    }
  };

  /* ── adjust dialog ── */
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  const openAdjust = (product) => {
    setAdjustTarget(product);
    setAdjustQty('');
    setAdjustReason('');
    setAdjustError('');
    setAdjustOpen(true);
  };

  const handleAdjust = async () => {
    if (!adjustQty || adjustQty === '0') { setAdjustError('Enter a non-zero quantity.'); return; }
    if (!adjustReason.trim()) { setAdjustError('Reason is required.'); return; }
    try {
      setAdjustLoading(true);
      setAdjustError('');
      await adjustStock(adjustTarget._id, { quantity: Number(adjustQty), reason: adjustReason });
      setAdjustOpen(false);
      load({ pg: page });
    } catch (err) {
      setAdjustError(err.response?.data?.message || 'Failed to adjust stock.');
    } finally {
      setAdjustLoading(false);
    }
  };

  /* ── history dialog ── */
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (product) => {
    setHistoryTarget(product);
    setHistoryData([]);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await fetchInventoryHistory(product._id);
      const raw = res.data;
      setHistoryData(raw?.transactions || raw?.history || (Array.isArray(raw) ? raw : []));
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1e293b">Inventory Management</Typography>
          <Typography variant="body2" color="text.secondary">Track stock levels, restock, and adjust inventory</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => load({ pg: page })} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {lowStockCount > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2, borderRadius: 2 }}>
          <strong>{lowStockCount} item(s)</strong> are at low or zero stock and need attention.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Products', value: total, color: '#667eea', icon: <InventoryIcon /> },
          { label: 'Low Stock', value: lowStockCount, color: '#f59e0b', icon: <Warning /> },
          { label: 'Out of Stock (page)', value: outOfStock, color: '#ef4444', icon: <Remove /> },
          { label: 'Page Stock Value', value: KES(totalValue), color: '#22c55e', icon: <InventoryIcon /> },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: `2px solid ${stat.color}22` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: stat.color }}>
                  {stat.icon}
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {stat.label}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={900} color={stat.color}>{stat.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <TextField
          size="small"
          placeholder="Search by name or barcode..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={catFilter} label="Category" onChange={(e) => handleCat(e.target.value)}>
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Stock Status</InputLabel>
          <Select value={statusFilter} label="Stock Status" onChange={(e) => handleStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ok">OK</MenuItem>
            <MenuItem value="low">Low Stock</MenuItem>
            <MenuItem value="out">Out of Stock</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Product', 'SKU / Barcode', 'Category', 'Stock', 'UOM', 'Threshold', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>No products match the filters.</TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const stock = product.stock ?? 0;
                  const st = STATUS(stock);
                  const catName = product.category?.name || categories.find((c) => c._id === (product.category || product.categoryId))?.name || '—';
                  return (
                    <TableRow key={product._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {product.imageUrl || product.image ? (
                            <Box component="img" src={product.imageUrl || product.image}
                              sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="caption" fontWeight={700} color="#94a3b8">
                                {(product.name || 'P').slice(0, 1).toUpperCase()}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{product.sku || product.barcode || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{catName}</Typography></TableCell>
                      <TableCell>
                        <Typography fontWeight={700} color={stock === 0 ? 'error.main' : stock <= 5 ? 'warning.main' : 'success.main'}>
                          {stock}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{product.unit || product.uom || 'pcs'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{product.lowStockThreshold ?? product.reorderLevel ?? 5}</Typography></TableCell>
                      <TableCell><Chip label={st.label} color={st.color} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Restock">
                            <Button size="small" variant="contained" startIcon={<AddCircle />} onClick={() => openRestock(product)}
                              sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
                              Restock
                            </Button>
                          </Tooltip>
                          <Tooltip title="Adjust stock">
                            <Button size="small" variant="outlined" onClick={() => openAdjust(product)}
                              sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1 }}>
                              Adjust
                            </Button>
                          </Tooltip>
                          <Tooltip title="View history">
                            <IconButton size="small" onClick={() => openHistory(product)}>
                              <History fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={LIMIT}
            rowsPerPageOptions={[LIMIT]}
          />
        </TableContainer>
      )}

      {/* ─── Restock Dialog ─── */}
      <Dialog open={restockOpen} onClose={() => setRestockOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Restock: {restockTarget?.name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Quantity to Add" type="number" value={restockQty} onChange={(e) => setRestockQty(e.target.value)} fullWidth size="small" inputProps={{ min: 1 }} />
            <TextField label="Unit Cost (KES)" type="number" value={restockCost} onChange={(e) => setRestockCost(e.target.value)} fullWidth size="small" />
            <TextField label="Note / Batch Ref" value={restockNote} onChange={(e) => setRestockNote(e.target.value)} fullWidth size="small" />
            {restockError && <Alert severity="error">{restockError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRestockOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleRestock} disabled={restockLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
            {restockLoading ? <CircularProgress size={18} /> : 'Confirm Restock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Adjust Dialog ─── */}
      <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Adjust Stock: {adjustTarget?.name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Quantity (negative to remove)"
              type="number"
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
              fullWidth
              size="small"
              helperText="e.g. -3 to remove 3 units, +10 to add 10"
            />
            <TextField label="Reason (required)" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} fullWidth size="small" placeholder="e.g. Damaged, Expired, Audit correction" />
            {adjustError && <Alert severity="error">{adjustError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdjustOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleAdjust} disabled={adjustLoading} sx={{ borderRadius: 2 }}>
            {adjustLoading ? <CircularProgress size={18} /> : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── History Dialog ─── */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Inventory History: {historyTarget?.name}</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : historyData.length === 0 ? (
            <Alert severity="info">No history found for this product.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Date', 'Type', 'Qty Change', 'Stock After', 'Note', 'User'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((tx, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{new Date(tx.createdAt || tx.date).toLocaleDateString()}</TableCell>
                      <TableCell><Chip label={tx.type || tx.action || '—'} size="small" /></TableCell>
                      <TableCell sx={{ color: (tx.quantityChange ?? tx.quantity ?? 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                        {(tx.quantityChange ?? tx.quantity ?? 0) >= 0 ? '+' : ''}{tx.quantityChange ?? tx.quantity ?? 0}
                      </TableCell>
                      <TableCell>{tx.stockAfter ?? tx.newStock ?? '—'}</TableCell>
                      <TableCell><Typography variant="caption">{tx.note || tx.reason || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{tx.performedBy?.name || tx.user?.name || tx.cashier || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistoryOpen(false)} sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
